import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Plus, Minus, Loader2 } from 'lucide-react';
import { rulesApi, groupsApi } from '../../services/api';
import type { CatalogRule } from '../../types';

interface SelectedRule {
  catalog: CatalogRule;
  column: string;
  severity: string;
}

interface Props {
  groupId: number;
  groupName: string;
  projectName: string;
  onClose: () => void;
  onAdded: () => void;
}

const SEVERITY_OPTIONS = ['Critical', 'High', 'Medium', 'Low'];

const AddRuleToGroupModal: React.FC<Props> = ({ groupId, groupName, projectName, onClose, onAdded }) => {
  const [catalog, setCatalog] = useState<CatalogRule[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SelectedRule[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const params: Record<string, any> = { items_per_page: 100, page: 1 };
      if (search.trim()) params.search = search.trim();
      const resp = await rulesApi.list(params);
      const payload = resp.data.payload;
      const rules = payload?.rules || payload || [];
      setCatalog(Array.isArray(rules) ? rules : []);
    } catch {
      setCatalog([]);
    } finally {
      setCatalogLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchCatalog, 300);
    return () => clearTimeout(timer);
  }, [fetchCatalog]);

  const isSelected = (rule: CatalogRule) => selected.some((s) => s.catalog.id === rule.id);

  const addRule = (rule: CatalogRule) => {
    if (!isSelected(rule)) {
      setSelected((prev) => [...prev, { catalog: rule, column: '', severity: 'Medium' }]);
    }
  };

  const removeRule = (ruleId: number) => {
    setSelected((prev) => prev.filter((s) => s.catalog.id !== ruleId));
  };

  const updateSelected = (ruleId: number, field: 'column' | 'severity', value: string) => {
    setSelected((prev) =>
      prev.map((s) => (s.catalog.id === ruleId ? { ...s, [field]: value } : s)),
    );
  };

  const handleAdd = async () => {
    if (selected.length === 0) return;
    setSaving(true);
    setError('');
    try {
      const rules = selected.map((s) => ({
        rule: s.catalog.rule,
        args: {
          column: s.column || undefined,
          severity: s.severity,
        },
        description: s.catalog.description || '',
        package: s.catalog.package || '',
      }));
      await groupsApi.addRules(groupId, { project_name: projectName, group_name: groupName, rules });
      onAdded();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to add rules.');
    } finally {
      setSaving(false);
    }
  };

  const CATEGORY_COLORS: Record<string, string> = {
    Quality: 'bg-green-500/20 text-green-400',
    Validity: 'bg-blue-500/20 text-blue-400',
    Completeness: 'bg-yellow-500/20 text-yellow-400',
    Consistency: 'bg-purple-500/20 text-purple-400',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-cargo-surface border border-cargo-border rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-cargo-border">
          <h2 className="text-lg font-semibold text-cargo-text">Add Rules to Group</h2>
          <button onClick={onClose} className="text-cargo-muted hover:text-cargo-text">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex min-h-0">
          {/* Left — catalog */}
          <div className="flex-1 border-r border-cargo-border flex flex-col min-w-0">
            <div className="px-4 py-3 border-b border-cargo-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cargo-muted" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search rules..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text
                             placeholder-cargo-muted/50 text-sm focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {catalogLoading ? (
                <div className="py-12 text-center">
                  <Loader2 className="w-5 h-5 animate-spin text-cargo-accent mx-auto" />
                </div>
              ) : catalog.length === 0 ? (
                <p className="py-12 text-center text-cargo-muted text-sm">No rules found.</p>
              ) : (
                catalog.map((rule) => (
                  <div
                    key={rule.id}
                    className={`flex items-center justify-between px-4 py-2.5 border-b border-cargo-border/50 hover:bg-cargo-panel/50 transition-colors
                                ${isSelected(rule) ? 'opacity-50' : ''}`}
                  >
                    <div className="min-w-0 flex-1 mr-3">
                      <p className="text-sm text-cargo-text font-mono truncate">{rule.rule}</p>
                      {rule.description && (
                        <p className="text-xs text-cargo-muted truncate mt-0.5">{rule.description}</p>
                      )}
                      {rule.category && (
                        <span className={`inline-block mt-1 px-1.5 py-0.5 text-[10px] rounded ${CATEGORY_COLORS[rule.category] || 'bg-cargo-panel text-cargo-muted'}`}>
                          {rule.category}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => addRule(rule)}
                      disabled={isSelected(rule)}
                      className="p-1.5 rounded-lg text-cargo-accent hover:bg-cargo-accent/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right — selected */}
          <div className="w-[340px] flex flex-col min-w-0">
            <div className="px-4 py-3 border-b border-cargo-border">
              <h3 className="text-xs font-medium text-cargo-muted uppercase tracking-wider">
                Selected ({selected.length})
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              {selected.length === 0 ? (
                <p className="py-12 text-center text-cargo-muted text-sm">No rules selected.</p>
              ) : (
                selected.map((s) => (
                  <div key={s.catalog.id} className="px-4 py-3 border-b border-cargo-border/50 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-cargo-text font-mono truncate flex-1 mr-2">{s.catalog.rule}</p>
                      <button
                        onClick={() => removeRule(s.catalog.id)}
                        className="p-1 rounded text-red-400 hover:bg-red-400/10 transition-colors flex-shrink-0"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <input
                        value={s.column}
                        onChange={(e) => updateSelected(s.catalog.id, 'column', e.target.value)}
                        placeholder="Column name"
                        className="flex-1 px-2 py-1.5 rounded bg-cargo-panel border border-cargo-border text-cargo-text text-xs
                                   placeholder-cargo-muted/50 focus:border-cargo-accent outline-none"
                      />
                      <select
                        value={s.severity}
                        onChange={(e) => updateSelected(s.catalog.id, 'severity', e.target.value)}
                        className="px-2 py-1.5 rounded bg-cargo-panel border border-cargo-border text-cargo-text text-xs
                                   focus:border-cargo-accent outline-none cursor-pointer"
                      >
                        {SEVERITY_OPTIONS.map((sev) => (
                          <option key={sev} value={sev}>{sev}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        {error && <p className="px-6 py-2 text-xs text-red-400">{error}</p>}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-cargo-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={saving || selected.length === 0}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-cargo-accent text-white hover:bg-cargo-accent/90
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Add {selected.length} Rule{selected.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRuleToGroupModal;
